"use client";

import PracticeChat from "@/components/PracticeChat";
import { ResponsiveLanguagePicker } from "@/components/language/ResponsiveLanguagePicker";
import languages from "@/data/languages.json";
import { getSeoPracticeLinksForLanguage } from "@/lib/seo/internalLinks";
import { getWritingPracticeDetails } from "@/lib/seo/writingPracticeDetails";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  Brain,
  Check,
  CheckCircle2,
  ChevronDown,
  Download,
  Eye,
  Flame,
  Globe,
  GraduationCap,
  MessageCircle,
  Mic,
  PenLine,
  Send,
  Sparkles,
  Target,
  Trophy,
  X,
  Zap,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React, { useMemo, useState } from "react";

type LanguageLevel = "absolute_beginner" | "beginner" | "intermediate" | "advanced";

const LEVELS: { value: LanguageLevel; label: string; description: string }[] = [
  { value: "absolute_beginner", label: "Absolute Beginner", description: "A0" },
  { value: "beginner", label: "Beginner", description: "A1-A2" },
  { value: "intermediate", label: "Intermediate", description: "B1-B2" },
  { value: "advanced", label: "Advanced", description: "C1-C2" },
];

// Sample translation exercises for the demo widget
// Format: English sentence -> User translates to target language
const DEMO_EXERCISES: Record<
  string,
  {
    english: string;
    correctAnswer: string;
    userAttempt: string;
    feedback: string;
    corrections: string[];
  }
> = {
  "Spanish (Spain)": {
    english: "I'm meeting my friends at the café later.",
    correctAnswer: "Voy a quedar con mis amigos en el café más tarde.",
    userAttempt: "Voy a ver mis amigos en el cafe después.",
    feedback: "Good attempt! A few adjustments:",
    corrections: [
      "**quedar con** instead of 'ver' for planned meetings",
      "**el café** needs the accent mark",
      "**más tarde** is more natural than 'después' here",
    ],
  },
  French: {
    english: "I drink coffee every morning before work.",
    correctAnswer: "Je bois du café tous les matins avant le travail.",
    userAttempt: "Je bois café chaque matin avant travail.",
    feedback: "Almost there! Key corrections:",
    corrections: [
      "**du café** - need the partitive article",
      "**tous les matins** is more natural than 'chaque matin'",
      "**le travail** - need the article before 'travail'",
    ],
  },
  German: {
    english: "Yesterday I went to the store and bought milk.",
    correctAnswer: "Gestern bin ich zum Laden gegangen und habe Milch gekauft.",
    userAttempt: "Gestern ich gehe zum Laden und kaufe Milch.",
    feedback: "Good try! Here's what to adjust:",
    corrections: [
      "**bin...gegangen** - past tense uses 'sein' + past participle",
      "**habe...gekauft** - past tense structure",
      "Verb position: **bin ich** (verb before subject in past)",
    ],
  },
  Italian: {
    english: "I've been living in Rome for three years.",
    correctAnswer: "Vivo a Roma da tre anni.",
    userAttempt: "Ho vissuto a Roma per tre anni.",
    feedback: "Interesting! In Italian:",
    corrections: [
      "Use **present tense + da** for ongoing situations",
      "**Vivo...da** = I've been living (still ongoing)",
      "'Ho vissuto' implies you no longer live there",
    ],
  },
  Japanese: {
    english: "I want to eat sushi tomorrow.",
    correctAnswer: "\u660e\u65e5\u5bff\u53f8\u3092\u98df\u3079\u305f\u3044\u3067\u3059\u3002",
    userAttempt: "\u660e\u65e5\u5bff\u53f8\u98df\u3079\u305f\u3044\u3002",
    feedback: "Close! Small adjustments:",
    corrections: [
      "Add particle **\u3092** after \u5bff\u53f8",
      "Add **\u3067\u3059** for polite form",
      "Good word order! \u660e\u65e5 (tomorrow) at the start is natural",
    ],
  },
  "Mandarin Chinese": {
    english: "Can you speak a little slower?",
    correctAnswer: "\u4f60\u53ef\u4ee5\u8bf4\u6162\u4e00\u70b9\u5417\uff1f",
    userAttempt: "\u4f60\u8bf4\u8bdd\u6162\u4e00\u70b9\u3002",
    feedback: "Almost! Here's how to make it a polite request:",
    corrections: [
      "Add **\u53ef\u4ee5** for 'can you'",
      "Add **\u5417** at the end for a question",
      "Good use of **\u6162\u4e00\u70b9** (a little slower)!",
    ],
  },
  "Portuguese (Brazil)": {
    english: "I'm going to visit my parents this weekend.",
    correctAnswer: "Vou visitar meus pais neste fim de semana.",
    userAttempt: "Eu vou visitar meus pais no fim de semana.",
    feedback: "Very close! Minor tweaks:",
    corrections: [
      "**neste** (this) is more specific than 'no' (the)",
      "The 'Eu' is optional - **Vou** alone is fine",
      "Great job with the verb structure!",
    ],
  },
  Korean: {
    english: "The weather is very nice today.",
    correctAnswer: "\uc624\ub298 \ub0a0\uc528\uac00 \uc544\uc8fc \uc88b\uc544\uc694.",
    userAttempt: "\uc624\ub298 \ub0a0\uc528 \ub9e4\uc6b0 \uc88b\ub2e4.",
    feedback: "Good attempt! Let's polish it:",
    corrections: [
      "Add **\uac00** particle after \ub0a0\uc528",
      "**\uc544\uc8fc** is more conversational than \ub9e4\uc6b0",
      "Use **\uc88b\uc544\uc694** for polite speech level",
    ],
  },
  default: {
    english: "I drink coffee every morning.",
    correctAnswer: "[Translation in your chosen language]",
    userAttempt: "[Your attempt here]",
    feedback: "The AI will analyze your translation:",
    corrections: [
      "Grammar and sentence structure",
      "Natural word choices and expressions",
      "Corrections with explanations",
    ],
  },
};

// FAQ data for the micro tool
const FAQS = [
  {
    question: "Is this really free?",
    answer:
      "Yes, ChickyTutor's Writing Practice tool is completely free. Get AI-powered feedback on your writing without any subscription required.",
  },
  {
    question: "What languages are supported?",
    answer:
      "Over 70 languages including Spanish, French, German, Italian, Japanese, Korean, Mandarin Chinese, Portuguese, Arabic, Russian, Hindi, Thai, Vietnamese, and many more.",
  },
  {
    question: "Do I need an account?",
    answer:
      "No, you can start practicing immediately without any registration. Creating a free account lets you save your writing history and track progress over time.",
  },
  {
    question: "How does the AI feedback work?",
    answer:
      "Our AI tutor analyzes your writing for grammar, vocabulary, and natural expression. You receive instant corrections with explanations, plus suggestions to improve your writing style.",
  },
  {
    question: "Can I practice conversation writing?",
    answer:
      "Yes! You can practice informal chat-style writing or formal compositions. The AI adapts to your chosen context and provides appropriate feedback.",
  },
];

// Lead Magnet Popup Component
function LeadMagnetPopup({
  isOpen,
  onClose,
  selectedLanguage,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedLanguage: string;
}) {
  const seoLinks = getSeoPracticeLinksForLanguage(selectedLanguage);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative bg-gradient-to-br from-[#1c202e] to-[#252a3d] rounded-2xl border border-white/10 p-8 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
                <Download size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Get Your Free Writing Guide!</h3>
              <p className="text-gray-400 mb-6">
                Download our comprehensive {selectedLanguage} writing guide with 50+ prompts, common
                phrases, and grammar tips.
              </p>

              <Link
                href={seoLinks.speakingPracticePath ?? "/"}
                className="block w-full py-3 px-6 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-bold rounded-xl transition-all text-center mb-3"
              >
                Try Full AI Tutor Free
              </Link>

              <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-300">
                Maybe later
              </button>

              <p className="text-xs text-gray-500 mt-4">
                - AI conversations - Grammar correction - 70+ languages
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Hero Demo Widget - Non-interactive example matching writing practice style
function HeroDemoWidget({
  selectedLanguage,
  onLanguageChange,
  onStartFullPractice,
}: {
  selectedLanguage: string;
  onLanguageChange: (lang: string) => void;
  onStartFullPractice: () => void;
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const currentExercise = DEMO_EXERCISES[selectedLanguage] || DEMO_EXERCISES.default;

  return (
    <div className="bg-[#1a1d2e] rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
      {/* Header bar matching chat style */}
      <div className="bg-[#252a3d] px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
            <PenLine size={16} className="text-white" />
          </div>
          <span className="text-white font-medium text-sm">Writing Practice</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span className="px-2 py-1 bg-white/5 rounded">AI Tutor</span>
          <span className="px-2 py-1 bg-orange-500/20 text-orange-300 rounded">Preview</span>
        </div>
      </div>

      <div className="p-6">
        {/* Language Selector - All 70+ languages */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-400 mb-2">
            Select Language to Practice
          </label>
          <ResponsiveLanguagePicker
            open={isDropdownOpen}
            onOpenChange={setIsDropdownOpen}
            trigger={
              <button
                type="button"
                className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-left transition-colors hover:bg-white/10"
              >
                <span className="font-medium text-white">{selectedLanguage}</span>
                <ChevronDown
                  size={20}
                  className={`text-gray-400 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>
            }
            languages={languages as Record<string, string>}
            selectedLanguageCode={(languages as Record<string, string>)[selectedLanguage] ?? ""}
            onSelectLanguage={(code) => {
              const entry = Object.entries(languages as Record<string, string>).find(
                ([, languageCode]) => languageCode === code
              );
              if (entry) onLanguageChange(entry[0]);
            }}
            searchTerm={searchQuery}
            onSearchTermChange={setSearchQuery}
            title="Choose a writing language"
          />
        </div>

        {/* Translation Exercise Display */}
        <motion.div
          key={selectedLanguage}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4 mb-6"
        >
          {/* English Sentence to Translate */}
          <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-5 border border-blue-500/20">
            <div className="flex items-center gap-2 mb-3">
              <Globe size={16} className="text-blue-400" />
              <span className="text-xs font-medium text-blue-300 uppercase tracking-wider">
                Translate to {selectedLanguage}
              </span>
            </div>
            <p className="text-xl sm:text-2xl text-white font-medium leading-relaxed">
              "{currentExercise.english}"
            </p>
          </div>

          {/* User's Attempt */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center">
                <PenLine size={12} className="text-gray-300" />
              </div>
              <span className="text-xs text-gray-400">Your attempt:</span>
            </div>
            <p className="text-white italic">"{currentExercise.userAttempt}"</p>
          </div>

          {/* AI Feedback */}
          <div className="bg-gradient-to-br from-orange-500/10 to-pink-500/10 rounded-xl p-5 border border-orange-500/20">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                <GraduationCap size={14} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium mb-3">{currentExercise.feedback}</p>
                <ul className="space-y-2">
                  {currentExercise.corrections.map((correction: string, index: number) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-2 text-sm text-gray-300"
                    >
                      <CheckCircle2 size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                      <span
                        dangerouslySetInnerHTML={{
                          __html: correction.replace(
                            /\*\*(.*?)\*\*/g,
                            '<strong class="text-orange-300">$1</strong>'
                          ),
                        }}
                      />
                    </motion.li>
                  ))}
                </ul>
                <div className="mt-4 pt-3 border-t border-white/10">
                  <p className="text-xs text-gray-400 mb-1">Correct answer:</p>
                  <p className="text-green-300 font-medium">"{currentExercise.correctAnswer}"</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Features Preview */}
        <div className="grid grid-cols-3 gap-2 mb-6">
          {[
            { icon: <Check size={14} />, label: "Grammar Check" },
            { icon: <Brain size={14} />, label: "AI Feedback" },
            { icon: <Target size={14} />, label: "Suggestions" },
          ].map((feature) => (
            <div
              key={feature.label}
              className="flex items-center justify-center gap-1.5 py-2 bg-white/5 rounded-lg text-xs text-gray-400"
            >
              <span className="text-green-400">{feature.icon}</span>
              {feature.label}
            </div>
          ))}
        </div>

        {/* Start Practice Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onStartFullPractice}
          className="w-full py-4 rounded-xl font-bold text-lg bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
        >
          <PenLine size={20} />
          <span>Start Writing Practice</span>
          <ArrowRight size={18} />
        </motion.button>
      </div>
    </div>
  );
}

// Comparison Table Component
function ComparisonTable() {
  const features = [
    {
      feature: "Pricing",
      competitor: "Subscription ($15-30/mo)",
      chickytutor: "100% Free",
      highlight: true,
    },
    {
      feature: "Account Required",
      competitor: "Yes, required",
      chickytutor: "No, instant start",
      highlight: true,
    },
    {
      feature: "AI Feedback",
      competitor: "Limited corrections",
      chickytutor: "Detailed explanations",
      highlight: true,
    },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left py-4 px-4 text-gray-400 font-medium">Feature</th>
            <th className="text-center py-4 px-4 text-gray-400 font-medium">Other Tools</th>
            <th className="text-center py-4 px-4 text-orange-400 font-medium">ChickyTutor</th>
          </tr>
        </thead>
        <tbody>
          {features.map((row, index) => (
            <tr
              key={index}
              className={`border-b border-white/5 ${row.highlight ? "bg-orange-500/5" : ""}`}
            >
              <td className="py-4 px-4 text-white font-medium">{row.feature}</td>
              <td className="py-4 px-4 text-center text-gray-400">{row.competitor}</td>
              <td className="py-4 px-4 text-center">
                <span
                  className={`inline-flex items-center gap-1 ${row.highlight ? "text-orange-300 font-medium" : "text-gray-300"}`}
                >
                  {row.highlight && <CheckCircle2 size={16} className="text-green-400" />}
                  {row.chickytutor}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// FAQ Component
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="space-y-4">
      {FAQS.map((faq, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.1 }}
          className="bg-white/5 rounded-xl border border-white/10 overflow-hidden"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full flex items-center justify-between p-4 text-left"
          >
            <span className="text-white font-medium">{faq.question}</span>
            <ChevronDown
              size={20}
              className={`text-gray-400 transition-transform ${openIndex === index ? "rotate-180" : ""}`}
            />
          </button>
          <AnimatePresence>
            {openIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <p className="px-4 pb-4 text-gray-400">{faq.answer}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}
    </div>
  );
}

type WritingPracticeToolClientProps = {
  initialLanguage?: string;
  pageMode?: "general" | "language";
};

// Main Page Component
export default function WritingPracticeToolClient({
  initialLanguage,
  pageMode = "general",
}: WritingPracticeToolClientProps) {
  const languageMap = languages as Record<string, string>;
  const defaultLanguage = "Spanish (Spain)";
  const resolvedInitialLanguage =
    initialLanguage && languageMap[initialLanguage] ? initialLanguage : defaultLanguage;
  const [selectedLanguage, setSelectedLanguage] = useState(resolvedInitialLanguage);
  const [selectedLanguageCode, setSelectedLanguageCode] = useState(
    languageMap[resolvedInitialLanguage] ?? "es-ES"
  );
  const [selectedLevel, setSelectedLevel] = useState<LanguageLevel>("beginner");
  const [hasStarted, setHasStarted] = useState(false);
  const [showLeadMagnet, setShowLeadMagnet] = useState(false);
  const writingDetails = getWritingPracticeDetails(selectedLanguage);
  const seoLinks = useMemo(
    () => getSeoPracticeLinksForLanguage(selectedLanguage),
    [selectedLanguage]
  );

  const handleLanguageChange = (lang: string) => {
    setSelectedLanguage(lang);
    const code = languageMap[lang];
    if (code) setSelectedLanguageCode(code);
  };

  const handleStartPractice = () => {
    setHasStarted(true);
  };

  const handleClose = () => {
    setHasStarted(false);
  };

  // If practice has started, show the PracticeChat component
  if (hasStarted) {
    return (
      <div className="relative h-screen overflow-hidden bg-gradient-to-br from-[#0f1117] via-[#131620] to-[#0f1117]">
        <PracticeChat
          languageName={selectedLanguage}
          languageLevel={selectedLevel}
          showLearningCanvas={false}
          onClose={handleClose}
        />
        {/* Floating CTA for Speaking Practice */}
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 2, type: "spring" }}
          className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:w-auto"
        >
          <Link
            href={seoLinks.speakingPracticePath ?? "/"}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
          >
            <Mic size={18} />
            <span>Try Speaking Practice</span>
            <ArrowRight size={16} />
          </Link>
        </motion.div>

        <LeadMagnetPopup
          isOpen={showLeadMagnet}
          onClose={() => setShowLeadMagnet(false)}
          selectedLanguage={selectedLanguage}
        />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-[#0f1117] via-[#131620] to-[#0f1117]">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          {/* Background effects */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-500/10 via-transparent to-transparent" />
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-72 h-72 bg-pink-500/5 rounded-full blur-3xl" />

          <div className="max-w-6xl mx-auto px-4 py-16 relative z-10">
            {/* Breadcrumb */}
            <nav className="mb-8">
              <ol className="flex items-center gap-2 text-sm text-gray-400">
                <li>
                  <Link href="/" className="hover:text-orange-400 transition-colors">
                    Home
                  </Link>
                </li>
                <li>/</li>
                <li>
                  <Link href="/tools" className="hover:text-orange-400 transition-colors">
                    Tools
                  </Link>
                </li>
                <li>/</li>
                <li className="text-orange-400">Writing Practice</li>
              </ol>
            </nav>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: Headline & Copy */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-center lg:text-left"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-full mb-6">
                  <Sparkles size={16} className="text-orange-400" />
                  <span className="text-sm font-medium text-orange-300">Free AI Writing Tool</span>
                </div>

                {pageMode === "language" ? (
                  <>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                      Practice Writing in{" "}
                      <span className="bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">
                        {selectedLanguage}
                      </span>
                    </h1>

                    <p className="text-xl text-gray-300 mb-8 max-w-xl mx-auto lg:mx-0">
                      Write in {selectedLanguage}, translate short prompts, and get instant AI
                      feedback on grammar, vocabulary, and natural phrasing.
                    </p>
                  </>
                ) : (
                  <>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                      Practice Writing in{" "}
                      <span className="bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">
                        70+ Languages
                      </span>
                    </h1>

                    <p className="text-xl text-gray-300 mb-8 max-w-xl mx-auto lg:mx-0">
                      Get instant AI feedback on your writing. Grammar corrections, vocabulary
                      suggestions, and style improvements-all completely free with no account
                      required.
                    </p>
                  </>
                )}

                <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-8">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Globe size={16} className="text-orange-400" />
                    <span>70+ Languages</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Brain size={16} className="text-orange-400" />
                    <span>AI Feedback</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Trophy size={16} className="text-orange-400" />
                    <span>Progress Tracking</span>
                  </div>
                </div>
              </motion.div>

              {/* Right: Interactive Widget */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <HeroDemoWidget
                  selectedLanguage={selectedLanguage}
                  onLanguageChange={handleLanguageChange}
                  onStartFullPractice={handleStartPractice}
                />
              </motion.div>
            </div>
          </div>
        </section>

        {pageMode === "language" ? (
          <section className="max-w-6xl mx-auto px-4 py-16 border-t border-white/5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 rounded-2xl border border-white/10 p-8"
            >
              <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-4">
                    What to focus on in {selectedLanguage} writing
                  </h2>
                  <p className="text-gray-300 leading-7">{writingDetails.focus}.</p>
                  <p className="mt-5 text-sm font-semibold uppercase tracking-[0.18em] text-orange-300">
                    Starter prompt
                  </p>
                  <p className="mt-2 text-gray-300 leading-7">{writingDetails.prompt}</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {writingDetails.corrections.map((item) => (
                    <div
                      key={item}
                      className="rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-gray-200"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-8 grid gap-4 md:grid-cols-2">
                {writingDetails.examples.map((example) => (
                  <article
                    key={example.label}
                    className="rounded-2xl border border-white/10 bg-black/20 p-5"
                  >
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-orange-300">
                      {example.label}
                    </p>
                    <p className="mt-4 text-sm text-gray-400">Learner attempt</p>
                    <p className="mt-1 text-white">{example.learner}</p>
                    <p className="mt-4 text-sm text-gray-400">AI correction</p>
                    <p className="mt-1 text-white">{example.repair}</p>
                    <p className="mt-4 text-sm leading-6 text-gray-300">{example.note}</p>
                  </article>
                ))}
              </div>
              <div className="mt-8 grid gap-4 md:grid-cols-2">
                {writingDetails.faqs.map((faq) => (
                  <article
                    key={faq.question}
                    className="rounded-2xl border border-orange-500/20 bg-orange-500/5 p-5"
                  >
                    <h3 className="text-base font-semibold text-white">{faq.question}</h3>
                    <p className="mt-3 text-sm leading-6 text-gray-300">{faq.answer}</p>
                  </article>
                ))}
              </div>
              {(seoLinks.fillInTheGapPath ||
                seoLinks.speakingPracticePath ||
                seoLinks.languageTargetPath) && (
                <div className="mt-8 border-t border-white/10 pt-6">
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-orange-300">
                    Practice this another way
                  </p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-4">
                    <Link
                      href={seoLinks.aiHubPath}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10"
                    >
                      <Sparkles size={16} />
                      <span>AI tutors</span>
                    </Link>
                    {seoLinks.fillInTheGapPath && (
                      <Link
                        href={seoLinks.fillInTheGapPath}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10"
                      >
                        <BookOpen size={16} />
                        <span>Fill gaps</span>
                      </Link>
                    )}
                    {seoLinks.speakingPracticePath && (
                      <Link
                        href={seoLinks.speakingPracticePath}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10"
                      >
                        <Mic size={16} />
                        <span>Speak</span>
                      </Link>
                    )}
                    {seoLinks.languageTargetPath && (
                      <Link
                        href={seoLinks.languageTargetPath}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-orange-500/30 bg-orange-500/10 px-4 py-3 text-sm font-semibold text-orange-200 hover:bg-orange-500/20"
                      >
                        <GraduationCap size={16} />
                        <span>Learn</span>
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </section>
        ) : null}

        {/* Comparison Section */}
        <section className="max-w-4xl mx-auto px-4 py-16 border-t border-white/5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Choose ChickyTutor for Writing Practice?
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              See how our free AI writing tool compares to paid alternatives.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-white/5 rounded-2xl border border-white/10 p-6"
          >
            <ComparisonTable />
          </motion.div>
        </section>

        {/* Core Features Section */}
        <section className="max-w-6xl mx-auto px-4 py-16 border-t border-white/5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How Translation Practice Works
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Translate sentences from English to your target language and get instant AI feedback.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Globe className="text-blue-400" size={32} />,
                title: "1. Read the Sentence",
                description:
                  "You receive an English sentence to translate. Sentences progress in difficulty as you improve.",
              },
              {
                icon: <PenLine className="text-orange-400" size={32} />,
                title: "2. Write Your Translation",
                description:
                  "Type your translation in the target language. Don't worry about perfection - mistakes are learning opportunities!",
              },
              {
                icon: <Brain className="text-pink-400" size={32} />,
                title: "3. Get AI Feedback",
                description:
                  "Receive instant corrections with explanations. Learn why natives say it differently and build intuition.",
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 rounded-2xl border border-white/10 p-6 hover:border-orange-500/30 transition-colors"
              >
                <div className="w-16 h-16 rounded-xl bg-white/5 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Speaking Practice Upsell Section */}
        <section className="max-w-4xl mx-auto px-4 py-16 border-t border-white/5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-orange-500/10 rounded-2xl border border-purple-500/20 p-8 text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-full mb-6">
              <Mic size={16} className="text-purple-400" />
              <span className="text-sm font-medium text-purple-300">Premium Feature</span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Practice Speaking?
            </h2>
            <p className="text-gray-300 text-lg mb-6 max-w-2xl mx-auto">
              Writing is great for building grammar intuition, but{" "}
              <strong className="text-white">speaking is where fluency happens</strong>. Practice
              real-time conversations with our AI voice tutor.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-8 max-w-2xl mx-auto">
              {[
                { icon: <Mic size={20} />, text: "Voice conversations" },
                { icon: <Zap size={20} />, text: "Instant pronunciation feedback" },
                { icon: <MessageCircle size={20} />, text: "Natural dialogues" },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-center gap-2 text-sm text-gray-300"
                >
                  <span className="text-purple-400">{item.icon}</span>
                  {item.text}
                </div>
              ))}
            </div>

            <Link
              href={seoLinks.speakingPracticePath ?? "/"}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold rounded-xl text-lg shadow-lg hover:shadow-xl transition-all"
            >
              <Mic size={20} />
              <span>Try Speaking Practice</span>
              <ArrowRight size={18} />
            </Link>
            <p className="text-xs text-gray-500 mt-4">
              Start with a free trial - No credit card required
            </p>
          </motion.div>
        </section>

        {/* Use Cases Section */}
        <section className="max-w-6xl mx-auto px-4 py-16 border-t border-white/5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Perfect For Every Learner
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Students",
                desc: "Homework help & essay practice",
                icon: <BookOpen size={24} />,
              },
              { title: "Travelers", desc: "Prepare for your next trip", icon: <Globe size={24} /> },
              {
                title: "Professionals",
                desc: "Business communication",
                icon: <MessageCircle size={24} />,
              },
              {
                title: "Hobbyists",
                desc: "Enjoy learning at your pace",
                icon: <Flame size={24} />,
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-gradient-to-br from-white/5 to-white/[0.02] rounded-xl border border-white/10 p-5 text-center"
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-orange-500/10 flex items-center justify-center text-orange-400">
                  {item.icon}
                </div>
                <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="max-w-3xl mx-auto px-4 py-16 border-t border-white/5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <FAQSection />
        </section>

        {/* Final CTA Section */}
        <section className="max-w-4xl mx-auto px-4 py-16 border-t border-white/5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Improve Your Writing?
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Start practicing now with AI-powered feedback. No signup required - just pick a
              language and begin writing!
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleStartPractice}
                className="px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-bold rounded-xl text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
              >
                <PenLine size={20} />
                Start Writing Practice
                <ArrowRight size={18} />
              </motion.button>

              <Link
                href={seoLinks.speakingPracticePath ?? "/"}
                className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold rounded-xl text-lg shadow-lg transition-all flex items-center justify-center gap-2"
              >
                <Mic size={20} />
                Try Speaking Practice
              </Link>
            </div>

            <div className="mt-6">
              <Link
                href={seoLinks.fillInTheGapPath ?? "/tools/fill-in-the-gap-language-practice"}
                className="text-gray-400 hover:text-orange-400 transition-colors text-sm inline-flex items-center gap-1"
              >
                Or try our free Fill-in-the-Gap exercises
                <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/5 py-8">
          <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Image
                src="/apple-touch-icon.png"
                alt="ChickyTutor"
                width={32}
                height={32}
                className="rounded-full"
              />
              <span className="text-gray-400 text-sm">
                (c) 2026 ChickyTutor. Free language learning for everyone.
              </span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link href="/blog" className="hover:text-orange-400 transition-colors">
                Blog
              </Link>
              <Link href="/terms" className="hover:text-orange-400 transition-colors">
                Terms
              </Link>
              <Link href="/" className="hover:text-orange-400 transition-colors">
                Main App
              </Link>
            </div>
          </div>
        </footer>

        <LeadMagnetPopup
          isOpen={showLeadMagnet}
          onClose={() => setShowLeadMagnet(false)}
          selectedLanguage={selectedLanguage}
        />
      </div>
    </>
  );
}
